<template>
  <dt-stack gap="600">
    <dt-stack gap="450">
      <h2 class="dp3-fs-200 d-lh-200">Color Mode</h2>
      <div class="d-d-grid d-g-cols3 d-gg8">
        <dt-button
          v-for="option in themeModeOptions"
          :key="option.value"
          importance="outlined"
          kind="muted"
          class="d-gg8 d-hmn42 h:d-bgc-transparent dp3-appearance-option"
          :class="{ 'dp3-appearance-option--selected': selectedTheme === option.value }"
          labelClass="d-fl0 dp3-fs-150"
          @click="handleThemeChange(option.value as ThemeType)"
        >
          <template #icon>
            <component :is="option.icon" size="300" />
          </template>
          {{ option.displayName }}
        </dt-button>
      </div>
      <dt-toggle
        size="sm"
        labelClass="d-pr16 dp3-fs-150 d-c-pointer"
        wrapperClass="d-w100p"
        :model-value="selectedContrastMode === 'high'"
        @change="handleContrastToggle"
      >
        High Contrast
      </dt-toggle>
    </dt-stack>

    <dt-stack gap="450">
      <h2 class="dp3-fs-200 d-lh-200">Color Themes</h2>
      <div class="d-d-grid d-g-cols3 d-gg8">
        <dt-button
          v-for="theme in availableColorThemes"
          :key="theme.name"
          importance="outlined"
          kind="muted"
          class="d-p0 h:d-bgc-transparent dp3-appearance-option"
          :class="{ 'dp3-appearance-option--selected': selectedColorTheme === theme.name }"
          labelClass="d-d-flex d-fd-column d-fl1 d-m2 d-of-hidden dp3-fs-150 dp3-appearance-option__theme"
          @click="handleColorThemeChange(theme.name)"
        >
          <span
            class="d-d-flex d-w100p d-h42 dp3-appearance-option__theme-preview"
            :style="{ backgroundColor: theme.previewColor }"
          ></span>
          <span class="d-py6">{{ theme.displayName }}</span>
        </dt-button>
      </div>
    </dt-stack>

    <dt-stack gap="450">
      <dt-radio-group
        v-model="selectedDensity"
        name="appearance-density"
        legend="Density"
        legendClass="dp3-fs-200 d-lh-200"
        class="dp3-appearance-option__density"
      >
        <dt-radio
          v-for="density in availableDensities"
          :key="density.name"
          :value="density.name"
          class="dp3-appearance-option__density-item"
          :class="{ 'dp3-appearance-option--selected': selectedDensity === density.name }"
          :label="density.displayName"
        />
      </dt-radio-group>
    </dt-stack>
  </dt-stack>
</template>

<script setup lang="ts">
import { ref, onMounted, markRaw, watch } from 'vue';
import { DtStack, DtButton, DtToggle, DtRadioGroup, DtRadio } from '@dialpad/dialtone/vue3';
import { DtIconLaptop2, DtIconSun, DtIconMoon } from '@dialpad/dialtone-icons/vue3';
import { useDebugLogger } from '@/utils/debugLogger';
import {
  applyTheme,
  applyColorTheme,
  applyDensity,
  applyContrastMode,
  getCurrentTheme,
  getCurrentColorTheme,
  getCurrentDensity,
  getCurrentContrastMode,
  getAvailableColorThemes,
  getAvailableDensities,
  type ThemeType,
  type ColorThemeName,
  type DensityName,
} from '@/utils/themeManager';

const debugLogger = useDebugLogger();

// Theme mode options with icons and display names
const themeModeOptions: Array<{
  value: ThemeType;
  displayName: string;
  icon: any;
  description: string;
}> = [
  {
    value: 'system',
    displayName: 'System',
    icon: markRaw(DtIconLaptop2),
    description: 'Matches your system preference',
  },
  {
    value: 'light',
    displayName: 'Light',
    icon: markRaw(DtIconSun),
    description: 'Light color scheme',
  },
  {
    value: 'dark',
    displayName: 'Dark',
    icon: markRaw(DtIconMoon),
    description: 'Dark color scheme',
  },
];

// Track the selected theme with a ref
const selectedTheme = ref(localStorage.getItem('theme') || 'system');

// Track the selected color theme with a ref
const selectedColorTheme = ref(getCurrentColorTheme());

// Track the selected density with a ref
const selectedDensity = ref(getCurrentDensity());
const selectedContrastMode = ref(getCurrentContrastMode());

// Get available color themes and densities
const availableColorThemes = getAvailableColorThemes();
const availableDensities = getAvailableDensities();

// Function to handle theme changes
const handleThemeChange = (theme: ThemeType) => {
  selectedTheme.value = theme;
  applyTheme(theme);
};

// Function to handle color theme changes
const handleColorThemeChange = (themeName: ColorThemeName) => {
  debugLogger.log('Changing color theme to:', themeName);
  selectedColorTheme.value = themeName;
  applyColorTheme(themeName);

  // Force a refresh of styles
  document.documentElement.classList.add('theme-changing');
  setTimeout(() => {
    document.documentElement.classList.remove('theme-changing');
  }, 10);
};

// Function to handle density changes
const handleDensityChange = (densityName: DensityName) => {
  debugLogger.log('Changing UI density to:', densityName);
  selectedDensity.value = densityName;
  applyDensity(densityName);

  // Force a refresh of styles
  document.documentElement.classList.add('density-changing');
  setTimeout(() => {
    document.documentElement.classList.remove('density-changing');
  }, 10);
};

const handleContrastToggle = async (enabled: boolean) => {
  selectedContrastMode.value = enabled ? 'high' : 'normal';
  await applyContrastMode(selectedContrastMode.value);
};

// Watch for density changes and apply them automatically
watch(selectedDensity, newDensity => {
  handleDensityChange(newDensity);
});

// Initialize the component on mount
onMounted(() => {
  // Get theme from localStorage and immediately apply it
  const storedTheme = (localStorage.getItem('theme') || 'system') as ThemeType;
  selectedTheme.value = storedTheme;
  applyTheme(storedTheme);

  // Get color theme from localStorage
  selectedColorTheme.value = getCurrentColorTheme();

  // Get density from localStorage
  selectedDensity.value = getCurrentDensity();

  // Get contrast mode from localStorage
  selectedContrastMode.value = getCurrentContrastMode();
});
</script>

<style lang="less">
.d-btn--muted.d-btn--outlined.dp3-appearance-option {
  &:hover {
    --button-color-border: hsla(var(--dt-color-border-brand-strong-hsl) / 0.5);
  }
  &--selected {
    --button-color-border: var(--dt-color-border-brand-strong);
  }
  &:hover,
  &--selected {
    box-shadow: inset 0 0 0 var(--dt-size-100) var(--button-color-border);
    transition: box-shadow 100ms ease-in-out;
  }
}

.dp3-appearance-option__theme {
  border-radius: calc(var(--button-border-radius) - var(--dt-size-200));
}
</style>
